<!--
  Share a game: who has access, and who can open it.

  Modelled on the dialog everyone already knows — a list of people each with a
  role dropdown, then one control for general access, then Copy link. The
  shape is familiar on purpose: sharing is the thing people are most afraid of
  getting wrong, so it should not need learning.

  Two rules it makes visible rather than explaining:
    * only the owner can change access, so this is owner-only
    * `playtester` is the default, because making somebody an editor just so
      they can look at a game hands them write access to your rules
-->
<template>
  <!-- Teleported to <body> deliberately. theme.css sets
       `#app > * { position: relative; z-index: 1 }` to lift page content above
       the paper texture, and the game page renders this dialog as a direct
       child of #app — so that rule beat `position: fixed` and the dialog was
       laid out at the bottom of the page instead of over it. -->
  <Teleport to="body">
    <div class="sd-backdrop" @click.self="close">
    <div class="sd" role="dialog" aria-modal="true" aria-label="Share game">
      <header class="sd-head">
        <h2 class="sd-title">Share &ldquo;{{ gameName }}&rdquo;</h2>
        <button class="sd-x" type="button" aria-label="Close" @click="close">&times;</button>
      </header>

      <div v-if="error" class="sd-error">{{ error }}</div>

      <!-- Invite -->
      <form class="sd-invite" @submit.prevent="invite">
        <input
          v-model.trim="email"
          type="email"
          class="sd-input"
          placeholder="Add people by email"
          :disabled="busy"
        />
        <select v-model="role" class="sd-role" :disabled="busy">
          <option v-for="r in ROLES" :key="r.value" :value="r.value">{{ r.label }}</option>
        </select>
        <button class="sd-btn" type="submit" :disabled="!email || busy">Invite</button>
      </form>
      <p class="sd-hint">{{ roleHint(role) }}</p>

      <!-- People with access -->
      <h3 class="sd-sub">People with access</h3>
      <ul class="sd-people">
        <li class="sd-person">
          <span class="sd-avatar sd-avatar--owner">{{ initial(ownerLabel) }}</span>
          <span class="sd-who">
            <span class="sd-email">{{ ownerLabel }}</span>
            <span class="sd-meta">Owner</span>
          </span>
          <span class="sd-fixed">Owner</span>
        </li>

        <li v-for="c in collaborators" :key="c.email" class="sd-person">
          <span class="sd-avatar">{{ initial(c.email) }}</span>
          <span class="sd-who">
            <span class="sd-email">{{ c.email }}</span>
            <span class="sd-meta">
              {{ c.status === 'accepted' ? 'Has access' : 'Invited — not accepted yet' }}
            </span>
          </span>
          <select
            class="sd-role sd-role--inline"
            :value="c.role"
            :disabled="busy"
            @change="changeRole(c, $event.target.value)"
          >
            <option v-for="r in ROLES" :key="r.value" :value="r.value">{{ r.label }}</option>
            <option value="__remove">Remove access</option>
          </select>
        </li>

        <li v-if="!collaborators.length" class="sd-empty">
          Nobody else yet. Only you can open this.
        </li>
      </ul>

      <!-- General access -->
      <h3 class="sd-sub">General access</h3>
      <div class="sd-general">
        <span class="sd-avatar" :class="`sd-avatar--${visibility}`">{{ visIcon }}</span>
        <span class="sd-who">
          <select
            class="sd-role sd-role--vis"
            :value="visibility"
            :disabled="busy"
            @change="changeVisibility($event.target.value)"
          >
            <option value="private">Restricted</option>
            <option value="playtest">Anyone with the link</option>
            <option value="public">Public</option>
          </select>
          <span class="sd-meta">{{ VISIBILITY[visibility] }}</span>
        </span>
      </div>

      <footer class="sd-foot">
        <button
          v-if="shareUrl"
          class="sd-ghost"
          type="button"
          :disabled="busy"
          @click="rotate"
          title="Everyone you already sent the link to loses access"
        >
          Reset link
        </button>
        <span class="sd-spacer" />
        <button class="sd-ghost" type="button" :disabled="!copyTarget" @click="copy">
          {{ copied ? 'Copied' : 'Copy link' }}
        </button>
        <button class="sd-btn" type="button" @click="close">Done</button>
      </footer>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { sparkforgeApi } from '@/sparkforgeApi'

const ROLES = [
  { value: 'playtester', label: 'Playtester' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' }
]

// What each level actually means, in the words a person would use. Kept short
// enough to sit under the dropdown without being read as a warning.
const VISIBILITY = {
  private: 'Only people added here can open it.',
  playtest: 'Anyone with the link can open it. It stays unlisted.',
  public: 'Anyone can find and open it.'
}

const ROLE_HINTS = {
  editor: 'Can change the rules, cards, boards and player aids.',
  playtester: 'Can read the game and leave feedback, but not change it.',
  viewer: 'Can read it and nothing else.'
}

export default {
  name: 'ShareDialog',
  props: {
    gameId: { type: String, required: true },
    gameName: { type: String, default: 'this game' },
    ownerLabel: { type: String, default: 'You' }
  },
  emits: ['close', 'changed'],
  data () {
    return {
      ROLES,
      VISIBILITY,
      email: '',
      role: 'playtester',
      visibility: 'private',
      shareUrl: null,
      collaborators: [],
      busy: false,
      copied: false,
      error: null
    }
  },
  computed: {
    visIcon () {
      return { private: '🔒', playtest: '🔗', public: '🌍' }[this.visibility] || '🔒'
    },
    /**
     * A restricted game has no share link, but the page URL is still the thing
     * a collaborator needs — so Copy link stays useful at every level.
     */
    copyTarget () {
      return this.shareUrl || window.location.href
    }
  },
  async mounted () {
    await this.load()
  },
  methods: {
    roleHint (role) {
      return ROLE_HINTS[role] || ''
    },
    initial (text) {
      return (text || '?').trim().charAt(0).toUpperCase()
    },
    apply (view) {
      this.visibility = view.visibility
      this.shareUrl = view.shareUrl
      this.collaborators = view.collaborators || []
      this.$emit('changed', view)
    },
    async run (fn) {
      // Every call returns the whole access view, so one handler covers all of
      // them and the dialog can never drift from the server's answer.
      this.error = null
      this.busy = true
      try {
        this.apply(await fn())
      } catch (e) {
        this.error = e.message || 'That did not work.'
      } finally {
        this.busy = false
      }
    },
    load () {
      return this.run(() => sparkforgeApi.getAccess(this.gameId))
    },
    invite () {
      const email = this.email
      const role = this.role
      return this.run(async () => {
        const view = await sparkforgeApi.shareWith(this.gameId, email, role)
        this.email = ''
        return view
      })
    },
    changeRole (person, value) {
      if (value === '__remove') {
        return this.run(() => sparkforgeApi.removeAccess(this.gameId, person.email))
      }
      return this.run(() => sparkforgeApi.shareWith(this.gameId, person.email, value))
    },
    changeVisibility (value) {
      return this.run(() => sparkforgeApi.setVisibility(this.gameId, value))
    },
    rotate () {
      return this.run(() => sparkforgeApi.rotateShareLink(this.gameId))
    },
    async copy () {
      try {
        await navigator.clipboard.writeText(this.copyTarget)
        this.copied = true
        setTimeout(() => { this.copied = false }, 1600)
      } catch (e) {
        // Clipboard is blocked in some contexts; show the link so it can be
        // selected by hand rather than failing silently.
        this.error = this.copyTarget
      }
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.sd-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(31, 36, 48, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1080;
}

.sd {
  width: 100%;
  max-width: 30rem;
  max-height: 90vh;
  overflow-y: auto;
  background: var(--bg-white, #fefcf9);
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(31, 36, 48, 0.28);
  padding: 1.25rem 1.4rem 1rem;
}

.sd-head { display: flex; align-items: flex-start; gap: 0.5rem; }

.sd-title {
  font-family: var(--font-heading, sans-serif);
  font-size: 1.15rem;
  color: var(--ink, #2d2d2d);
  margin: 0 0 0.25rem;
  flex: 1 1 auto;
}

.sd-x {
  background: none; border: 0; line-height: 1;
  font-size: 1.5rem; color: var(--ink-muted, #8a8a8a); cursor: pointer;
}

.sd-error {
  background: #fdecee; color: #8c1d28;
  border-radius: 8px; padding: 0.5rem 0.7rem;
  font-size: 0.85rem; margin: 0.5rem 0; word-break: break-all;
}

.sd-invite { display: flex; gap: 0.4rem; margin-top: 0.75rem; }

.sd-input {
  flex: 1 1 auto; min-width: 0;
  border: 1px solid var(--border-sketch, rgba(0, 0, 0, 0.12));
  border-radius: 8px; padding: 0.45rem 0.6rem; font-size: 0.92rem;
}

.sd-role {
  border: 1px solid var(--border-sketch, rgba(0, 0, 0, 0.12));
  border-radius: 8px; padding: 0.45rem 0.4rem;
  font-size: 0.85rem; background: var(--bg-white, #fefcf9);
  color: var(--ink, #2d2d2d);
}

.sd-role--inline, .sd-role--vis { border-color: transparent; padding-left: 0.2rem; }
.sd-role--inline:hover, .sd-role--vis:hover {
  border-color: var(--border-sketch, rgba(0, 0, 0, 0.12));
}
.sd-role--vis { font-weight: 600; }

.sd-btn {
  background: var(--pop-orange, #ff6b35); color: #fff;
  border: 0; border-radius: 8px; padding: 0.45rem 0.9rem;
  font-size: 0.9rem; font-weight: 600; cursor: pointer;
}
.sd-btn:disabled { opacity: 0.5; cursor: default; }

.sd-ghost {
  background: none; border: 0; border-radius: 8px;
  padding: 0.45rem 0.6rem; font-size: 0.88rem;
  color: var(--ink-light, #5a5a5a); cursor: pointer;
}
.sd-ghost:hover { background: var(--bg-sketch, #eee8dd); }

.sd-hint {
  font-size: 0.78rem; color: var(--ink-muted, #8a8a8a);
  margin: 0.35rem 0 0;
}

.sd-sub {
  font-family: var(--font-heading, sans-serif);
  font-size: 0.82rem; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--ink-muted, #8a8a8a);
  margin: 1.25rem 0 0.5rem;
}

.sd-people { list-style: none; padding: 0; margin: 0; }

.sd-person, .sd-general {
  display: flex; align-items: center; gap: 0.65rem;
  padding: 0.4rem 0;
}

.sd-avatar {
  flex: 0 0 2rem; height: 2rem; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: var(--bg-sketch, #eee8dd);
  color: var(--ink-light, #5a5a5a);
  font-size: 0.85rem; font-weight: 600;
}
.sd-avatar--owner { background: var(--pop-orange, #ff6b35); color: #fff; }
.sd-avatar--public { background: #e6f4ec; }
.sd-avatar--playtest { background: #e7f0fa; }

.sd-who { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }

.sd-email {
  font-size: 0.92rem; color: var(--ink, #2d2d2d);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.sd-meta { font-size: 0.76rem; color: var(--ink-muted, #8a8a8a); }

.sd-fixed { font-size: 0.85rem; color: var(--ink-muted, #8a8a8a); padding-right: 0.4rem; }

.sd-empty { font-size: 0.85rem; color: var(--ink-muted, #8a8a8a); padding: 0.4rem 0; }

.sd-foot {
  display: flex; align-items: center; gap: 0.4rem;
  margin-top: 1.25rem; padding-top: 0.85rem;
  border-top: 1px solid var(--border-sketch, rgba(0, 0, 0, 0.08));
}
.sd-spacer { flex: 1 1 auto; }
</style>
